/**
 * The line that says how long an open room has (2026-10-17 Riley, 小屋留人需求 需求一): the room page's fact line
 * and the notebook's Rooms card read the same words. A room is kept its first days whatever happens
 * (`kept_until`); after them it closes three days after the last human line (`closes_at`, moved on by every
 * line). The line says the moment of the close, never a countdown, and always how to keep it; the last day it
 * is `urgent` (the accent colour). A room kept but quiet two days says so instead of "open".
 * The App's RoomTimer (iOS / Android) says the same.
 */

export interface RoomClock {
  status: string;
  closes_at: string | null;
  kept_until?: string | null;
  last_human_line_at?: string | null;
  opened_at?: string | null;
}

const QUIET_SAY_HOURS = 48;
const URGENT_HOURS = 24;
const H = 3600e3;

const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const clock = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: d.getMinutes() ? '2-digit' : undefined });

/** "today 3 PM" / "tomorrow 9 AM" / "Thu 9 AM" / "Oct 24, 9 AM". */
function whenLine(at: Date, now: Date): string {
  const days = Math.round((dayStart(at) - dayStart(now)) / 86400e3);
  if (days <= 0) return `today ${clock(at)}`;
  if (days === 1) return `tomorrow ${clock(at)}`;
  if (days < 7) return `${at.toLocaleDateString('en-US', { weekday: 'short' })} ${clock(at)}`;
  return `${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${clock(at)}`;
}

/** "Tuesday" within the week, else "Oct 12". */
function sinceLine(at: Date, now: Date): string {
  const days = Math.round((dayStart(now) - dayStart(at)) / 86400e3);
  if (days === 1) return 'yesterday';
  if (days < 7) return at.toLocaleDateString('en-US', { weekday: 'long' });
  return at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * The room's rules, as the ? beside the timer says them (2026-10-17 Riley; the App's RoomTimer.rules / ROOM_RULES).
 * Since 2026-10-26 (founder) every room closes after a week with no one talking (edge ROOM.CLOSE_QUIET_HOURS); the ten
 * kept days and the three quiet days went.
 */
export const ROOM_RULES = 'A room stays open while people are talking. It closes once no one has said anything for a week. Any line keeps it open, and Ember tells you the day before. Once closed, you can still read it.';
/** The same rules for a chat between two new friends (2026-10-27 walk-through: a pair's ? spoke of "a room" and "people"). */
export const PAIR_RULES = 'Your chat stays open while you two are talking. It closes once neither of you has said anything for a week. Any line keeps it open, and Ember tells you the day before. Once closed, you can still read it.';

/** The words for an open room; null when the room is not open or carries no clock. */
export function roomTimer(room: RoomClock, now: Date = new Date()): { text: string; urgent: boolean } | null {
  if (room.status !== 'open' || !room.closes_at) return null;
  const closes = new Date(room.closes_at);
  if (!Number.isFinite(closes.getTime())) return null;
  const kept = room.kept_until ? new Date(room.kept_until) : null;
  if (kept && now < kept) {
    const last = room.last_human_line_at || room.opened_at;
    const lastAt = last ? new Date(last) : null;
    if (lastAt && now.getTime() - lastAt.getTime() >= QUIET_SAY_HOURS * H) return { text: `quiet since ${sinceLine(lastAt, now)}`, urgent: false };
    return { text: `kept open until ${kept.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, urgent: false };
  }
  return { text: `closes ${whenLine(closes, now)} · a line keeps it open`, urgent: closes.getTime() - now.getTime() <= URGENT_HOURS * H };
}
